import React from "react";
import { useLocation, Navigate } from "react-router-dom";
import { Check, MapPin, ArrowRight, ShieldCheck, UserCheck, CalendarClock, Languages, ChevronRight, Phone } from "lucide-react";
import Seo from "../components/Seo";
import BookingForm from "../components/BookingForm";
import CTABand from "../components/CTABand";
import { Link } from "../components/L";
import { getLandingData, landingsForCity, INTENTS, baseCitySlug } from "../landingPages";
import { contactInfo, IMAGES } from "../mock";
import { useLang } from "../context/LanguageContext";

const iconMap = { ShieldCheck, UserCheck, CalendarClock, Languages };

const LandingPage = () => {
  const { pathname } = useLocation();
  const { lang, t } = useLang();
  const slug = pathname.replace(/^\/en/, "").replace(/^\//, "");
  const data = getLandingData(slug, lang);
  if (!data) return <Navigate to="/staedte" replace />;

  const pc = (t._pages || {})[`landing:${slug}`] || {};
  const f = (k, d) => pc[k] || d;
  const kw = f("keyword", data.keyword).replace(/\?$/, "");

  const jsonLd = [
    {
      "@context": "https://schema.org", "@type": "LocalBusiness",
      name: `MyCleaner.ch – ${kw}`, description: data.meta.description,
      areaServed: data.cityName, priceRange: "CHF 43.90/hr", telephone: contactInfo.phone,
    },
    {
      "@context": "https://schema.org", "@type": "FAQPage",
      mainEntity: data.faqs.map((faq) => ({
        "@type": "Question", name: faq.q,
        acceptedAnswer: { "@type": "Answer", text: faq.a },
      })),
    },
  ];

  const siblings = landingsForCity(data.citySlug).filter((s) => s.slug !== slug);
  const siblingLabel = (s) => INTENTS[s.intentId][lang]({ name: data.cityName, q: "", quarters: data.quarters, canton: "", city: {} }).keyword.replace(/\?$/, "");

  return (
    <div>
      <Seo pageKey={`landing:${slug}`} title={data.meta.title} description={data.meta.description} keywords={data.meta.keywords} jsonLd={jsonLd} />

      {/* HERO */}
      <section className="pt-[116px] pb-14 bg-gradient-to-b from-amber-50 via-teal-50/40 to-white">
        <div className="max-w-[1200px] mx-auto px-5 md:px-6 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 font-semibold text-sm px-4 py-1.5 rounded-full mb-5">
              <MapPin className="w-4 h-4" /> {data.badge}
            </div>
            <h1 className="font-head font-extrabold text-4xl md:text-5xl leading-[1.1] text-slate-900 mb-5" data-testid="landing-h1">{f("keyword", data.keyword)}</h1>
            <p className="text-lg text-slate-600 leading-relaxed mb-7 max-w-xl">{f("lead", data.lead)}</p>
            <div className="flex flex-wrap gap-3">
              <Link to="/buchen" className="inline-flex items-center gap-2 bg-teal-700 text-white font-head font-bold text-[17px] px-7 py-4 rounded-xl hover:bg-teal-800 transition-colors shadow-lg shadow-teal-800/20">{t.hero.ctaBook} <ArrowRight className="w-5 h-5" /></Link>
              <a href={contactInfo.phoneHref} className="inline-flex items-center gap-2 bg-white border border-slate-200 text-slate-800 font-semibold text-[17px] px-7 py-4 rounded-xl hover:border-teal-600 hover:text-teal-700 transition-colors"><Phone className="w-5 h-5" /> {contactInfo.phone}</a>
            </div>
          </div>
          <div className="rounded-3xl overflow-hidden shadow-2xl">
            <img src={IMAGES.living} alt={kw} className="w-full h-[360px] object-cover" />
          </div>
        </div>
      </section>

      {/* SECTION 1 */}
      <section className="py-14">
        <div className="max-w-[900px] mx-auto px-5 md:px-6">
          <h2 className="font-head font-extrabold text-3xl text-slate-900 mb-4">{f("s1Title", data.s1Title)}</h2>
          <p className="text-slate-600 text-lg leading-relaxed">{f("s1Body", data.s1Body)}</p>
        </div>
      </section>

      {/* SECTION 2 + tasks/trust */}
      <section className="py-14 bg-amber-50/50">
        <div className="max-w-[1200px] mx-auto px-5 md:px-6 grid lg:grid-cols-2 gap-10 items-start">
          <div className="flex flex-col gap-8">
            <div>
              <h2 className="font-head font-extrabold text-3xl text-slate-900 mb-4">{f("s2Title", data.s2Title)}</h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-5">{f("s2Body", data.s2Body)}</p>
              <ul className="space-y-3">
                {data.s2List.map((li) => (
                  <li key={li} className="flex items-start gap-3"><span className="w-6 h-6 rounded-full bg-teal-600 flex items-center justify-center shrink-0 mt-0.5"><Check className="w-3.5 h-3.5 text-white" /></span><span className="text-slate-700 text-[15px]">{li}</span></li>
                ))}
              </ul>
            </div>
            <div className="space-y-5 pt-4 border-t border-teal-100">
              {t.trustPoints.map((tp) => {
                const Icon = iconMap[tp.icon];
                return (
                  <div key={tp.title} className="flex items-start gap-3"><span className="w-9 h-9 rounded-lg bg-white flex items-center justify-center shrink-0 border border-slate-100"><Icon className="w-4 h-4 text-teal-700" /></span><div><div className="font-semibold text-slate-900 text-sm">{tp.title}</div><div className="text-slate-500 text-[13px]">{tp.desc}</div></div></div>
                );
              })}
            </div>
          </div>
          <BookingForm defaultCity={baseCitySlug(data.citySlug)} title={`${kw} – 1h ${lang === "de" ? "gratis" : "free"}`} />
        </div>
      </section>

      {/* SECTION 3 + quarters */}
      <section className="py-14 bg-[#faf6ee]">
        <div className="max-w-[900px] mx-auto px-5 md:px-6">
          <h2 className="font-head font-semibold text-[#12211b] text-3xl mb-4">{f("s3Title", data.s3Title)}</h2>
          <p className="text-slate-600 text-lg leading-relaxed mb-8">{f("s3Body", data.s3Body)}</p>
          <div className="flex flex-wrap gap-2.5">
            {data.quarters.map((qr) => (
              <span key={qr} className="inline-flex items-center gap-2 bg-white border border-[#ece4d3] rounded-full px-4 py-2 text-slate-700 text-sm">
                <MapPin className="w-3.5 h-3.5 text-[#b8893f]" /> {data.cityName} – {qr}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 bg-amber-50/50">
        <div className="max-w-[820px] mx-auto px-5 md:px-6">
          <h2 className="font-head font-extrabold text-slate-900 text-3xl text-center mb-10">{lang === "de" ? `Häufige Fragen: ${kw}` : `FAQ: ${kw}`}</h2>
          <div className="space-y-4">
            {data.faqs.map((faq) => (
              <details key={faq.q} className="group bg-white rounded-xl border border-slate-100 p-5 open:shadow-md transition-shadow">
                <summary className="flex items-center justify-between cursor-pointer font-head font-semibold text-slate-900 list-none">{faq.q}<ChevronRight className="w-5 h-5 text-teal-600 group-open:rotate-90 transition-transform" /></summary>
                <p className="text-slate-600 text-[15px] leading-relaxed mt-3">{faq.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* INTERLINKS */}
      <section className="py-14">
        <div className="max-w-[1200px] mx-auto px-5 md:px-6">
          <h2 className="font-head font-extrabold text-2xl text-slate-900 mb-6">{lang === "de" ? `Mehr für ${data.cityName}` : `More for ${data.cityName}`}</h2>
          <div className="flex flex-wrap gap-3">
            <Link to={`/putzfrau/${data.citySlug}`} className="inline-flex items-center gap-2 bg-teal-700 text-white rounded-full px-5 py-2.5 hover:bg-teal-800 transition-colors">
              {t.term} {data.cityName} <ChevronRight className="w-4 h-4" />
            </Link>
            {siblings.map((s) => (
              <Link key={s.slug} to={`/${s.slug}`} className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-5 py-2.5 text-slate-700 hover:border-teal-600 hover:text-teal-700 transition-colors">
                <MapPin className="w-4 h-4 text-teal-600" /> {siblingLabel(s)} <ChevronRight className="w-4 h-4 text-slate-300" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTABand city={data.cityName} />
    </div>
  );
};

export default LandingPage;
